import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { PollingProvider } from './contexts/PollingContext';
import { StartupLoadingProvider, useStartupLoading } from './contexts/StartupLoadingContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import FloatingUploadWidget from './components/FloatingUploadWidget';
import StartupLoadingScreen from './components/StartupLoadingScreen';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import GlobalUpgradeBanner from './components/GlobalUpgradeBanner';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import StuckStateDetector from './components/StuckStateDetector';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import VideoDetails from './pages/VideoDetails';
import ClipDetails from './pages/ClipDetails';
import PublicClip from './pages/PublicClip';
import Upload from './pages/Upload';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import AuthAction from './pages/AuthAction';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Feedback from './pages/Feedback';
import Tools from './pages/Tools';
import ClipConverterPage from './pages/ClipConverter';
import AudioSeparation from './pages/AudioSeparation';
import OAuthCallback from './pages/OAuthCallback';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import FAQ from './pages/Faq';
import Help from './pages/Help';
import Contact from './pages/Contact';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Tutorials from './pages/Tutorials';
import TutorialDetail from './pages/TutorialDetail';
import ClipsOverview from './pages/ClipsOverview';
import VideosOverview from './pages/VideosOverview';
import LocalizedLanding from './pages/LocalizedLanding';
import { keepAliveService } from './services/keepAlive';
import { useBackendWarmup } from './hooks/useBackendWarmup';
import { useAuth } from './contexts/AuthContext';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { usePageTracking, useUserAnalytics } from './hooks/useAnalytics';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import OfflineIndicator from './components/OfflineIndicator';

// Check if we're running on production (not localhost)
const isProduction = typeof window !== 'undefined' &&
  !window.location.hostname.includes('localhost') &&
  !window.location.hostname.includes('127.0.0.1');

// Shutdown screen for production
const ShutdownScreen: React.FC = () => (
  <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center p-6">
    <div className="max-w-lg w-full text-center">
      {/* Logo */}
      <div className="flex items-center justify-center mb-8">
        <span className="text-4xl font-display font-bold tracking-tighter">
          CLIPTHIS
        </span>
        <div className="ml-2 w-3 h-3 bg-black rounded-full" />
      </div>

      {/* Message */}
      <h1 className="text-2xl font-semibold text-gray-900 mb-4">
        Thanks for everything
      </h1>
      <p className="text-gray-600 text-lg mb-3">
        ClipThis has been shut down.
      </p>
      <p className="text-gray-500 mb-10">
        Running costs became too expensive to maintain. It was a fun ride while it lasted — thank you to everyone who used the app.
      </p>

      {/* CTA */}
      <a
        href="https://x.com/A5omic"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        Message me on X @A5omic
      </a>
    </div>
  </div>
);

// Inner App component that uses the startup loading context
const AppContent: React.FC = () => {
  const { isLoading: isStartupLoading, estimatedTime, completeStartup } = useStartupLoading();
  const { ensureBackendWarm } = useBackendWarmup();
  const { loading: authLoading } = useAuth();
  const { maintenanceMode, loading: maintenanceLoading } = useMaintenanceMode();

  // All hooks must be called before any conditional returns
  useKeyboardShortcuts();
  usePageTracking();
  useUserAnalytics();

  // Start keep-alive service and check backend warmup when app loads
  React.useEffect(() => {
    keepAliveService.start();
    
    // Check if backend needs warmup on first load
    ensureBackendWarm();
    
    // Cleanup on unmount
    return () => {
      keepAliveService.stop();
    };
  }, [ensureBackendWarm]);

  // Add global development functions for auto-redirect control
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).disableAutoRedirect = () => {
        localStorage.setItem('disable_auto_redirect', 'true');
        // console.log('✅ Auto-redirect disabled. Navigate to / while logged in to test landing page.');
        // console.log('💡 Use window.enableAutoRedirect() to re-enable');
      };
      
      (window as any).enableAutoRedirect = () => {
        localStorage.setItem('disable_auto_redirect', 'false');
        // console.log('✅ Auto-redirect enabled. Logged-in users will be redirected to dashboard.');
      };
      
      (window as any).checkAutoRedirect = () => {
        const disabled = localStorage.getItem('disable_auto_redirect') === 'true';
        // console.log(`🔍 Auto-redirect is currently ${disabled ? 'DISABLED' : 'ENABLED'}`);
        // console.log(`💡 Use window.${disabled ? 'enableAutoRedirect' : 'disableAutoRedirect'}() to toggle`);
        return !disabled;
      };

      // Log available functions on app load
      // console.log('🛠️ Development functions available:');
      // console.log('  • window.disableAutoRedirect() - Test landing page while logged in');
      // console.log('  • window.enableAutoRedirect() - Restore normal redirect behavior');  
      // console.log('  • window.checkAutoRedirect() - Check current setting');
      // console.log('');
      // console.log('🚀 Quick commands:');
      // console.log('  • localStorage.setItem("disable_auto_redirect", "true") - Disable redirect');
      // console.log('  • localStorage.setItem("disable_auto_redirect", "false") - Enable redirect');
    }
  }, []);

  // Show shutdown screen in production (after all hooks)
  if (isProduction) {
    return <ShutdownScreen />;
  }

  return (
    <>
      {/* Maintenance Mode Overlay - shows over everything when enabled */}
      <MaintenanceOverlay 
        isVisible={!maintenanceLoading && maintenanceMode.enabled}
        title={maintenanceMode.title}
        message={maintenanceMode.message}
        estimatedDuration={maintenanceMode.estimatedDuration}
      />

      {/* Startup Loading Screen - shows over everything when backend is cold or auth is loading */}
      <StartupLoadingScreen 
        isVisible={isStartupLoading || authLoading}
        onComplete={completeStartup}
        estimatedTime={authLoading ? 5 : estimatedTime}
        isAuthLoading={authLoading}
      />

      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="pt-16 flex-1">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/downloads" element={<Tools />} />
            <Route path="/tools" element={<Navigate to="/downloads" replace />} />
            <Route path="/converter" element={<ClipConverterPage />} />
            <Route path="/audio" element={<AudioSeparation />} />
            <Route path="/features" element={<Features />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/clips" element={<ClipsOverview />} />
            <Route path="/videos" element={<VideosOverview />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/tutorials/:slug" element={<TutorialDetail />} />
            <Route path="/es" element={<LocalizedLanding locale="es" />} />
            <Route path="/fr" element={<LocalizedLanding locale="fr" />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/action" element={<AuthAction />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/oauth/youtube/callback" element={<OAuthCallback />} />
            <Route path="/oauth/tiktok/callback" element={<OAuthCallback />} />
            
            {/* Public clip sharing routes */}
            <Route path="/clips/public/:clipId" element={<PublicClip />} />
            <Route path="/clips/share/:shareToken" element={<PublicClip />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/videos/:videoId" element={<VideoDetails />} />
            <Route path="/clips/:clipId" element={<ClipDetails />} />
          </Routes>
        </main>
        
        {/* Floating upload widget appears globally */}
        <FloatingUploadWidget />
        
        {/* Global upgrade banner for free users */}
        <GlobalUpgradeBanner />
        
        {/* Stuck state detector for automatic cleanup */}
        <StuckStateDetector />
        
        {/* PWA Components */}
        <PWAInstallPrompt />
        <PWAUpdatePrompt />
        <OfflineIndicator />
        
        <ToastContainer
          position="bottom-left"
          autoClose={2500}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={false}
          theme="light"
          closeButton={true}
          enableMultiContainer={false}
          limit={5}
          toastClassName="!bg-white !border !rounded-xl !font-medium !text-sm !shadow-lg !mb-2 !min-h-[50px] !py-3 !px-4"
          bodyClassName="!flex !items-center !m-0 !p-0 !text-sm !leading-relaxed !flex-1"
          icon={({ type }) => {
            switch (type) {
              case 'success':
                return <span className="text-red-500 text-lg mr-3 flex-shrink-0">✓</span>;
              case 'error':
                return <span className="text-red-600 text-lg mr-3 flex-shrink-0">✕</span>;
              case 'warning':
                return <span className="text-gray-600 text-lg mr-3 flex-shrink-0">⚠</span>;
              case 'info':
                return <span className="text-red-500 text-lg mr-3 flex-shrink-0">✓</span>;
              default:
                return <span className="text-red-500 text-lg mr-3 flex-shrink-0">✓</span>;
            }
          }}
        />
      </div>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <UploadProvider>
            <PollingProvider>
              <StartupLoadingProvider>
                <AppContent />
              </StartupLoadingProvider>
            </PollingProvider>
          </UploadProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App; 